import React, { useState, useEffect } from 'react';
import { CreditCard, Plus, Edit2, Trash2, X, CheckCircle, AlertCircle } from 'lucide-react';
import mentorBankDetailsService from '../../services/mentorBankDetails.service';
import toast from 'react-hot-toast';
import Button from '@components/common/Button';

const BankDetailsSection = () => {
  const [bankDetails, setBankDetails] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    accountHolderName: '',
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    accountType: 'savings',
    branchName: '',
    panNumber: '',
    upiId: ''
  });

  useEffect(() => {
    fetchBankDetails();
  }, []);

  const fetchBankDetails = async () => {
    try {
      const response = await mentorBankDetailsService.getBankDetails();
      if (response.success && response.data) {
        setBankDetails(response.data);
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Failed to fetch bank details:', error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setLoading(true);

    try {
      const response = await mentorBankDetailsService.saveBankDetails(formData);
      toast.success(response.message || 'Bank details saved successfully');
      fetchBankDetails();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save bank details');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setFormData({
      accountHolderName: bankDetails.accountHolderName || '',
      bankName: bankDetails.bankName || '',
      accountNumber: bankDetails.accountNumber || '',
      ifscCode: bankDetails.ifscCode || '',
      accountType: bankDetails.accountType || 'savings',
      branchName: bankDetails.branchName || '',
      panNumber: bankDetails.panNumber || '',
      upiId: bankDetails.upiId || ''
    });
    setShowForm(true);
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete your bank details?')) return;

    try {
      await mentorBankDetailsService.deleteBankDetails();
      toast.success('Bank details deleted successfully');
      setBankDetails(null);
    } catch (error) {
      toast.error('Failed to delete bank details');
    }
  };

  const resetForm = () => {
    setFormData({
      accountHolderName: '',
      bankName: '',
      accountNumber: '',
      ifscCode: '',
      accountType: 'savings',
      branchName: '',
      panNumber: '',
      upiId: ''
    });
    setShowForm(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const maskAccountNumber = (accountNumber) => {
    if (!accountNumber) return '';
    const length = accountNumber.length;
    return accountNumber.substring(0, 4) + '****' + accountNumber.substring(length - 4);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold text-primary-800 flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
            <CreditCard className="w-4 h-4 text-primary-700" />
          </div>
          Bank Account Details
        </h3>
        {!showForm && !bankDetails && (
          <Button
            onClick={() => setShowForm(true)}
            variant="primary"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Bank Details
          </Button>
        )}
      </div>

      {/* Display Bank Details */}
      {!showForm && bankDetails && (
        <div className="space-y-4">
          <div className="p-4 bg-gradient-to-r from-primary-50 to-blue-50 rounded-xl border border-primary-100">
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-primary-600" />
                <h4 className="font-semibold text-primary-900">{bankDetails.bankName}</h4>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleEdit}
                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  title="Edit"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={handleDelete}
                  className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-gray-500 mb-1">Account Holder Name</p>
                <p className="text-sm font-medium text-gray-900">{bankDetails.accountHolderName}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Account Number</p>
                <p className="text-sm font-medium text-gray-900 font-mono">
                  {maskAccountNumber(bankDetails.accountNumber)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">IFSC Code</p>
                <p className="text-sm font-medium text-gray-900 font-mono">{bankDetails.ifscCode}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Account Type</p>
                <p className="text-sm font-medium text-gray-900 capitalize">{bankDetails.accountType}</p>
              </div>
              {bankDetails.branchName && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Branch</p>
                  <p className="text-sm font-medium text-gray-900">{bankDetails.branchName}</p>
                </div>
              )}
              {bankDetails.panNumber && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">PAN Number</p>
                  <p className="text-sm font-medium text-gray-900 font-mono">{bankDetails.panNumber}</p>
                </div>
              )}
              {bankDetails.upiId && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">UPI ID</p>
                  <p className="text-sm font-medium text-gray-900">{bankDetails.upiId}</p>
                </div>
              )}
            </div>

            <div className="mt-3 pt-3 border-t border-primary-100">
              {bankDetails.isVerified ? (
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-xs font-medium">Verified</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-orange-600">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-xs font-medium">Pending Verification</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="space-y-4 p-5 bg-gray-50 rounded-xl border border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-semibold text-primary-800">
              {bankDetails ? 'Edit Bank Details' : 'Add Bank Details'}
            </h4>
            <Button
              type="button"
              onClick={resetForm}
              variant="ghost"
              className="p-1"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-primary-800 mb-2">
                Account Holder Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="accountHolderName"
                value={formData.accountHolderName}
                onChange={handleChange}
                className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="As per bank records"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-primary-800 mb-2">
                Bank Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="bankName"
                value={formData.bankName}
                onChange={handleChange}
                className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g., State Bank of India"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-primary-800 mb-2">
                Account Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="accountNumber"
                value={formData.accountNumber}
                onChange={handleChange}
                className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono"
                placeholder="Enter account number"
                pattern="[0-9]{9,18}"
                title="Account number must be 9-18 digits"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-primary-800 mb-2">
                IFSC Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="ifscCode"
                value={formData.ifscCode}
                onChange={handleChange}
                className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono uppercase"
                placeholder="e.g., SBIN0001234"
                pattern="[A-Z]{4}0[A-Z0-9]{6}"
                title="IFSC code must be in format: XXXX0XXXXXX"
                maxLength="11"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-primary-800 mb-2">
                Account Type <span className="text-red-500">*</span>
              </label>
              <select
                name="accountType"
                value={formData.accountType}
                onChange={handleChange}
                className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
                required
              >
                <option value="savings">Savings</option>
                <option value="current">Current</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-primary-800 mb-2">
                Branch Name
              </label>
              <input
                type="text"
                name="branchName"
                value={formData.branchName}
                onChange={handleChange}
                className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Optional"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-primary-800 mb-2">
                PAN Number
              </label>
              <input
                type="text"
                name="panNumber"
                value={formData.panNumber}
                onChange={handleChange}
                className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono uppercase"
                placeholder="e.g., ABCDE1234F"
                pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
                title="PAN must be in format: AAAAA9999A"
                maxLength="10"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-primary-800 mb-2">
                UPI ID
              </label>
              <input
                type="text"
                name="upiId"
                value={formData.upiId}
                onChange={handleChange}
                className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g., yourname@paytm"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              disabled={loading}
              variant="primary"
              loading={loading}
            >
              {bankDetails ? 'Update' : 'Save'} Bank Details
            </Button>
            <Button
              type="button"
              onClick={resetForm}
              variant="outline"
            >
              Cancel
            </Button>
          </div>
        </form>
      )}

      {/* Empty State */}
      {!showForm && !bankDetails && (
        <div className="text-center py-12">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <CreditCard className="w-8 h-8 text-gray-400" />
          </div>
          <p className="text-gray-500 text-sm mb-4">No bank details added yet</p>
          <p className="text-xs text-gray-400 max-w-md mx-auto">
            Add your bank account details to receive payments when you answer student questions
          </p>
        </div>
      )}
    </div>
  );
};

export default BankDetailsSection;
